/**
 * PatientBillingTabBody — body of Patient Billing (tab 5 of the
 * patient-edit shell, PROD-733).
 *
 * Layout (Flutter ref `patient_invoices_list_screen.dart` +
 * `patient_advance_receipts_screen.dart`):
 *   ┌─────────────────────────────────────────────┐
 *   │ Available balance card                       │
 *   ├─────────────────────────────────────────────┤
 *   │ [Invoices] [Advance receipts] [Refunds]      │
 *   │                            [+ Add payment]   │
 *   │                                              │
 *   │ <list>                                       │
 *   └─────────────────────────────────────────────┘
 *
 * Sub-tab state is local. The Refunds sub-tab + Add Payment toolbar
 * button land in Tier-3 (`docs/CONVERSIONS/patient-billing-advance-flow/`).
 *
 * Mutations available:
 *  - Add Advance Payment — `AddAdvancePaymentDialog` opens from the
 *    "Add payment" toolbar button on the Advance receipts sub-tab.
 *  - Refund / Void per-row actions on receipts: deferred.
 */

import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { Button, SubTabs, type SubTabDef } from '@scripsteam/scrips-signal-ds';
import { AvailableBalanceCard } from './available-balance-card';
import { PatientInvoicesList } from './patient-invoices-list';
import { PatientAdvanceReceiptsList } from './patient-advance-receipts-list';
import { PatientRefundsList } from './patient-refunds-list';
import { AddAdvancePaymentDialog } from './add-advance-payment-dialog';

export interface PatientBillingTabBodyProps {
  patientId: string | null | undefined;
}

export function PatientBillingTabBody({ patientId }: PatientBillingTabBodyProps) {
  // PROD-2743 · switch ON: the DS `add` glyph (one icon system, 16px inline); OFF = today's lucide.
  const [addPaymentOpen, setAddPaymentOpen] = useState(false);

  // Sub-tab is URL-addressable via `?billingView=` so other surfaces can
  // deep-link into it — the appointment Billing tab's Available-Balance link
  // opens `receipts` directly (Flutter pushes PatientAdvanceReceiptsScreen).
  // Clamp to the known views; anything else → invoices.
  const [searchParams, setSearchParams] = useSearchParams();
  const rawView = searchParams.get('billingView');
  const billingView =
    rawView === 'receipts' || rawView === 'refunds' ? rawView : 'invoices';
  const setBillingView = (v: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('billingView', v);
    // Push, not replace (design log 12 Oct, Rule "browser Back / Forward"): every screen change
    // inside a patient is a history entry, so Back returns to the previous billing view.
    setSearchParams(next, { replace: false });
  };

  if (!patientId) {
    return (
      <div className="rounded-lg border border-dashed border-border-default p-8 text-center">
        <p className="text-sm text-text-muted">
          Save the patient first to see their billing history.
        </p>
      </div>
    );
  }

  const subTabs: SubTabDef[] = [
    { id: 'invoices', label: 'Invoices', panelId: 'billing-panel-invoices' },
    { id: 'receipts', label: 'Advance receipts', panelId: 'billing-panel-receipts' },
    { id: 'refunds', label: 'Refunds', panelId: 'billing-panel-refunds' },
  ];

  return (
    <div className="space-y-4 p-6">
      <AvailableBalanceCard patientId={patientId} />

      <div className="space-y-3">
        <SubTabs tabs={subTabs} value={billingView} onValueChange={setBillingView} />

        {billingView === 'invoices' ? (
          <div id="billing-panel-invoices" role="tabpanel" aria-labelledby="sub-tab-invoices">
            <PatientInvoicesList patientId={patientId} />
          </div>
        ) : null}
        {billingView === 'receipts' ? (
          <div id="billing-panel-receipts" role="tabpanel" aria-labelledby="sub-tab-receipts">
            <div className="mb-3 flex items-center justify-end">
              <Button
                type="button"
                size="sm"
                onClick={() => setAddPaymentOpen(true)}
              >
                <Plus className="mr-1 size-4" />
                Add payment
              </Button>
            </div>
            <PatientAdvanceReceiptsList patientId={patientId} />
          </div>
        ) : null}
        {billingView === 'refunds' ? (
          <div id="billing-panel-refunds" role="tabpanel" aria-labelledby="sub-tab-refunds">
            <PatientRefundsList patientId={patientId} />
          </div>
        ) : null}
      </div>

      <AddAdvancePaymentDialog
        open={addPaymentOpen}
        onOpenChange={setAddPaymentOpen}
        patientId={patientId}
      />
    </div>
  );
}
